"use client";

import { FeaturedIn } from "@/components/FeaturedIn";
import {
  Box,
  Container,
  Title,
  Text,
  Button,
  SimpleGrid,
  Stack,
  Paper,
  Modal,
  Flex,
  rem,
  Divider,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  IconCloudUpload,
  IconX,
  IconCamera,
  IconDownload,
} from "@tabler/icons-react";
import React, { useRef, useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { PRICING } from "@/constants/pricing";
import { trackToolPhotoUpload } from "@/lib/analytics";
import { WEEKLY_ANALYSES_FLOOR } from "@/lib/analysisCount";

export default function Content() {
  const router = useRouter();

  const [cameraOpen, { open: openCamera, close: closeCamera }] = useDisclosure(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [zoomImage, setZoomImage] = useState<string | null>(null);

  const isMobile = useMediaQuery("(max-width: 48em)");


  const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<string> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(maxWidth / width, maxHeight / height, 1);
        width = Math.round(width * scale);
        height = Math.round(height * scale);

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (ctx) ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        URL.revokeObjectURL(img.src);
        resolve(dataUrl);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handlePhotoDrop = async (files: File[]) => {
    if (files.length > 0) {
      trackToolPhotoUpload("Korean Color Analysis");
      const dataUrl = await resizeImage(files[0], 1200, 1200);
      sessionStorage.setItem("colorAnalysisImage", dataUrl);
      // Route to the shared result page
      router.push("/ai-color-analysis/result");
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (err) {
      console.error("Camera error:", err);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const takePhoto = async () => {
    if (videoRef.current && captureCanvasRef.current) {
      const video = videoRef.current;
      const canvas = captureCanvasRef.current;

      let width = video.videoWidth;
      let height = video.videoHeight;
      const scale = Math.min(1200 / width, 1200 / height, 1);
      width = Math.round(width * scale);
      height = Math.round(height * scale);

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(video, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        sessionStorage.setItem("colorAnalysisImage", dataUrl);
        closeCamera();
        stopCamera();
        router.push("/ai-color-analysis/result");
      }
    }
  };

  const handleOpenCamera = () => {
    openCamera();
    setTimeout(() => startCamera(), 100);
  };

  const handleCloseCamera = () => {
    stopCamera();
    closeCamera();
  };

  return (
    <Box mt={{ base: "1rem", md: "3.5rem" }} pos="relative">
      {/* Hero Section */}
      <Box pt={{ base: 0, md: "md" }} pb={{ base: "sm", md: "xl" }}>
        <Container size="lg">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: "md", md: "xl" }}>
            {/* Left: Copy */}
            <Stack gap="sm" justify="center">
              <Title
                order={1}
                fz={{ base: "1.5rem", md: "2.5rem" }}
                fw={700}
                style={{ lineHeight: 1.15, color: "#1E293B" }}
                ta={{ base: "center", md: "left" }}
              >
                Korean Personal Color Analysis
              </Title>
              <Text fz={{ base: "md", md: "lg" }} ta={{ base: "center", md: "left" }} style={{ color: "#64748B" }}>
                퍼스널 컬러 — Discover your color season the K-beauty way.
              </Text>
              <Text fz={{ base: "sm", md: "md" }} ta={{ base: "center", md: "left" }} style={{ color: "#64748B" }}>
                Find your primary & secondary color influences, warm/cool undertone, and the exact shades that make your skin glow.
              </Text>
              <Text fz="xs" ta={{ base: "center", md: "left" }} style={{ color: "#94A3B8" }}>
                No signup · ~2 min · Photos stay private
              </Text>
            </Stack>

            {/* Right: Upload Box */}
            <Paper
              radius="lg"
              p={{ base: "md", md: "xl" }}
              style={{
                background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
              }}
            >
              <Stack gap="sm">
                <Box ta="center">
                  <Text fw={600} fz={{ base: "md", md: "lg" }} style={{ color: "#1E293B" }}>
                    Upload Photo → Get Your 퍼스널 컬러
                  </Text>
                </Box>

                <Dropzone
                  onDrop={handlePhotoDrop}
                  maxSize={10 * 1024 ** 2}
                  accept={IMAGE_MIME_TYPE}
                  multiple={false}
                  h={130}
                  radius="lg"
                  styles={{
                    root: {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "white",
                      border: "2px dashed #CBD5E1",
                      "&:hover": {
                        borderColor: "#3B82F6",
                        backgroundColor: "#F8FAFC",
                      },
                    },
                  }}
                >
                  <Flex direction="column" align="center" justify="center" style={{ pointerEvents: "none" }}>
                    <Dropzone.Accept>
                      <IconDownload style={{ width: rem(36), height: rem(36), color: "#22C55E" }} stroke={1.5} />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                      <IconX style={{ width: rem(36), height: rem(36), color: "#EF4444" }} stroke={1.5} />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                      <IconCloudUpload style={{ width: rem(36), height: rem(36), color: "#94A3B8" }} stroke={1.5} />
                    </Dropzone.Idle>
                    <Text size="sm" ta="center" mt="xs" fw={500} style={{ color: "#475569" }}>
                      Drag photo here or click to select
                    </Text>
                    <Text size="xs" ta="center" mt={4} style={{ color: "#94A3B8" }}>
                      Best: shoulders-up, natural lighting, no makeup
                    </Text>
                  </Flex>
                </Dropzone>

                {!isMobile && (
                  <>
                    <Divider label="or" labelPosition="center" color="gray.3" my={0} />

                    <Button
                      variant="light"
                      color="blue"
                      size="md"
                      radius="md"
                      leftSection={<IconCamera size={18} />}
                      onClick={handleOpenCamera}
                      fullWidth
                    >
                      Use Your Camera
                    </Button>
                  </>
                )}
              </Stack>
            </Paper>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Social Proof */}
      <Box py={{ base: "sm", md: "lg" }} bg="#F8FAFC">
        <Container size="md" ta="center">
          <Title order={2} fz={{ base: "1rem", md: "1.75rem" }} mb={4} style={{ color: "#1E293B" }}>
            <Text span fz={{ base: "1.25rem", md: "2rem" }} fw={700} style={{ color: "#3B82F6" }}>
              {WEEKLY_ANALYSES_FLOOR}+
            </Text>{" "}
            personal color analyses run every week
          </Title>
          <Box mt={{ base: "xs", md: "sm" }} mb={{ base: "sm", md: "lg" }}>
            <FeaturedIn />
          </Box>
        </Container>
      </Box>

      {/* What is Korean Color Analysis */}
      <Box py={{ base: "md", md: "xl" }}>
        <Container size="md">
          <Title order={2} ta="center" mb={{ base: "sm", md: "md" }} fz={{ base: "1.1rem", md: "1.75rem" }} style={{ color: "#1E293B" }}>
            What is Korean Personal Color (퍼스널 컬러)?
          </Title>

          {/* Hero Image */}
          <Box
            mb="lg"
            style={{
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              maxWidth: 500,
              margin: "0 auto 24px",
            }}
          >
            <img
              src="/korean-color-analysis/hero.png"
              alt="Korean personal color analysis with seasonal color draping"
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </Box>

          <Text ta="center" style={{ color: "#64748B", maxWidth: 700, margin: "0 auto" }} mb="lg">
            Korean personal color analysis goes beyond simple seasonal typing. It identifies your <strong>primary color season</strong> and <strong>secondary influence</strong>—because most people aren't purely one type. This nuanced approach, popular in K-beauty salons, helps you find colors that enhance skin clarity and create that coveted "glowing" look.
          </Text>

          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            {[
              { season: "Spring", korean: "봄 웜톤", desc: "Warm, bright, clear", colors: ["#FF6B6B", "#FFD93D", "#6BCB77"] },
              { season: "Summer", korean: "여름 쿨톤", desc: "Cool, soft, muted", colors: ["#A8D8EA", "#AA96DA", "#FCBAD3"] },
              { season: "Autumn", korean: "가을 웜톤", desc: "Warm, deep, muted", colors: ["#D4A373", "#BC6C25", "#606C38"] },
              { season: "Winter", korean: "겨울 쿨톤", desc: "Cool, bright, clear", colors: ["#2D3436", "#D63031", "#0984E3"] },
            ].map((item) => (
              <Paper key={item.season} withBorder p="md" radius="md" ta="center">
                <Text fw={700} fz="md" style={{ color: "#1E293B" }}>{item.season}</Text>
                <Text fz="sm" style={{ color: "#64748B" }}>{item.korean}</Text>
                <Text fz="xs" style={{ color: "#94A3B8" }} mt={4}>{item.desc}</Text>
                <Flex gap={4} justify="center" mt="sm">
                  {item.colors.map((c, i) => (
                    <Box key={i} w={20} h={20} style={{ backgroundColor: c, borderRadius: 4 }} />
                  ))}
                </Flex>
              </Paper>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* See Real Results */}
      <Box py={{ base: "sm", md: "lg" }} bg="#F8FAFC">
        <Container size="lg">
          <Title order={2} ta="center" mb={{ base: "xs", md: "md" }} fz={{ base: "1.1rem", md: "1.75rem" }} style={{ color: "#1E293B" }}>
            See Real Color Analysis Results
          </Title>
          {/* Mobile: horizontal scroll */}
          <Box
            hiddenFrom="sm"
            style={{
              display: "flex",
              gap: 12,
              overflowX: "auto",
              paddingBottom: 8,
              WebkitOverflowScrolling: "touch",
              scrollSnapType: "x mandatory",
            }}
          >
            {[
              "/swatchPreviews/season1.png",
              "/swatchPreviews/season2.png",
              "/swatchPreviews/season3.png",
            ].map((src, i) => (
              <Box
                key={i}
                onClick={() => setZoomImage(src)}
                style={{
                  flexShrink: 0,
                  width: 260,
                  background: "white",
                  borderRadius: 20,
                  padding: 8,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                  scrollSnapAlign: "start",
                  cursor: "pointer",
                }}
              >
                <Box style={{ borderRadius: 12, overflow: "hidden" }}>
                  <img
                    src={src}
                    alt={`Season result ${i + 1}`}
                    style={{ width: "100%", height: "auto", display: "block" }}
                    loading="lazy"
                  />
                </Box>
              </Box>
            ))}
          </Box>
          {/* Desktop: centered grid */}
          <SimpleGrid cols={3} spacing="lg" visibleFrom="sm">
            {[
              "/swatchPreviews/season1.png",
              "/swatchPreviews/season2.png",
              "/swatchPreviews/season3.png",
            ].map((src, i) => (
              <Box
                key={i}
                onClick={() => setZoomImage(src)}
                style={{
                  background: "white",
                  borderRadius: 20,
                  padding: 12,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.02)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              >
                <Box style={{ borderRadius: 12, overflow: "hidden" }}>
                  <img
                    src={src}
                    alt={`Season result ${i + 1}`}
                    style={{ width: "100%", height: "auto", display: "block" }}
                    loading="lazy"
                  />
                </Box>
              </Box>
            ))}
          </SimpleGrid>
          <Text fz="xs" ta="center" mt="xs" style={{ color: "#94A3B8" }}>
            Tap to view full result
          </Text>
        </Container>
      </Box>

      {/* Primary & Secondary Influence Explanation */}
      <Box py={{ base: "md", md: "xl" }}>
        <Container size="md">
          <Title order={2} ta="center" mb={{ base: "sm", md: "md" }} fz={{ base: "1.1rem", md: "1.75rem" }} style={{ color: "#1E293B" }}>
            Primary & Secondary Color Influence
          </Title>
          <Text ta="center" style={{ color: "#64748B", maxWidth: 600, margin: "0 auto" }} mb="lg">
            Unlike simple seasonal typing, Korean color analysis recognizes that your coloring may draw from multiple seasons. Your result shows both your dominant season and secondary influence.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Paper withBorder p="lg" radius="md">
              <Text fw={700} fz="md" mb="xs" style={{ color: "#1E293B" }}>Primary Influence (주요 영향)</Text>
              <Text fz="sm" style={{ color: "#64748B" }}>
                Your dominant color season. These colors will always look great on you—they're your "safe" palette for everyday wear, makeup, and hair color choices.
              </Text>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Text fw={700} fz="md" mb="xs" style={{ color: "#1E293B" }}>Secondary Influence (보조 영향)</Text>
              <Text fz="sm" style={{ color: "#64748B" }}>
                Your secondary season adds nuance. You can borrow colors from this palette for variety, and it explains why some "unexpected" colors also work for you.
              </Text>
            </Paper>
          </SimpleGrid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box py={{ base: "sm", md: "lg" }} bg="#F8FAFC">
        <Container size="xl">
          <Title order={2} ta="center" mb={{ base: "sm", md: "lg" }} style={{ color: "#1E293B" }}>
            Frequently Asked Questions
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {[
              {
                question: "What is Korean personal color (퍼스널 컬러)?",
                answer: "Korean personal color analysis identifies your seasonal color type and undertone to find the most flattering colors for your skin. It's extremely popular in Korea for choosing makeup, hair color, and clothing.",
              },
              {
                question: "How is this different from Western color analysis?",
                answer: "Korean color analysis emphasizes primary and secondary seasonal influences, skin clarity, and the warm/cool distinction. It's more nuanced and considers how colors affect your perceived skin brightness.",
              },
              {
                question: "Is this accurate for Asian skin tones?",
                answer: "Yes! Our AI has been trained on diverse skin tones including East Asian, Southeast Asian, and South Asian complexions. Korean color analysis principles work across all ethnicities.",
              },
              {
                question: "How much does it cost?",
                answer: `The preview is free. The full analysis with primary/secondary influences, 60+ colors, and outfit recommendations is $${PRICING.AI_COLOR_ANALYSIS.BASE_PRICE}.`,
              },
              {
                question: "What photo should I use?",
                answer: "Use a clear, front-facing photo in natural light. No heavy makeup, filters, or sunglasses. A bare face or minimal makeup gives the most accurate results.",
              },
              {
                question: "What will I learn from my results?",
                answer: "You'll discover your primary season, secondary influence, warm/cool undertone, contrast level, and get 60+ personalized color recommendations with hex codes.",
              },
            ].map((faq, index) => (
              <Paper key={index} p="lg" radius="lg" bg="white" style={{ border: "1px solid #E2E8F0" }}>
                <Title order={4} mb="sm" fz="md" style={{ color: "#1E293B" }}>
                  {faq.question}
                </Title>
                <Text fz="sm" style={{ color: "#64748B" }}>
                  {faq.answer}
                </Text>
              </Paper>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Camera Modal */}
      <Modal opened={cameraOpen} onClose={handleCloseCamera} title="Take a Selfie" centered size="md">
        <Stack>
          <Box h={350} style={{ overflow: "hidden", borderRadius: 12 }}>
            <video ref={videoRef} autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </Box>
          <Button onClick={takePhoto} leftSection={<IconCamera size={18} />} color="blue" size="lg" radius="md">
            Capture Photo
          </Button>
          <canvas ref={captureCanvasRef} style={{ display: "none" }} />
        </Stack>
      </Modal>

      {/* Screenshot Zoom Modal */}
      <Modal
        opened={!!zoomImage}
        onClose={() => setZoomImage(null)}
        size="xl"
        centered
        padding={0}
        withCloseButton={false}
        styles={{
          body: { padding: 0 },
          content: { background: "transparent", boxShadow: "none" },
        }}
        overlayProps={{ backgroundOpacity: 0.7 }}
      >
        <Box
          onClick={() => setZoomImage(null)}
          style={{
            cursor: "pointer",
            maxHeight: "90vh",
            overflow: "auto",
            borderRadius: 16,
            background: "white",
          }}
        >
          {zoomImage && (
            <img
              src={zoomImage}
              alt="Full preview"
              style={{ width: "100%", height: "auto", display: "block", borderRadius: 16 }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
}
